<template>
  <div
    ref="containerRef"
    class="relative flex h-[44rem] items-center justify-center p-2 md:h-[60rem] md:p-20"
  >
    <div class="relative w-full py-10 md:py-40" style="perspective: 1000px">
      <ContainerScrollTitle :translate="translateY">
        <slot name="title"></slot>
      </ContainerScrollTitle>
      <ContainerScrollCard :rotate="rotate" :scale="scale">
        <slot name="card" />
      </ContainerScrollCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useWindowSize, useScroll, useElementBounding } from '@vueuse/core'

const containerRef = ref(null)
const isMobile = ref(false)
const isClient = ref(false)

// Initialize VueUse composables with safe defaults for SSR
const { height } = useWindowSize({ initialWidth: 1024, initialHeight: 768 })
const { y: scrollY } = useScroll(import.meta.client ? window : null, {
  behavior: 'auto',
})
const { bottom } = useElementBounding(containerRef, {
  reset: false,
  windowResize: false,
  windowScroll: false,
  immediate: false,
})

function updateIsMobile() {
  if (import.meta.client && typeof window !== 'undefined') {
    isMobile.value = window.innerWidth <= 768
  }
}

onMounted(() => {
  // Wait for hydration to complete
  nextTick(() => {
    isClient.value = true
    updateIsMobile()

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateIsMobile, { passive: true })
    }
  })
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateIsMobile)
  }
})

const scrollYProgress = computed(() => {
  if (!isClient.value || !bottom.value || typeof window === 'undefined')
    return 0
  return 1 - Math.max(0, bottom.value - scrollY.value) / height.value
})

const scaleDimensions = computed(() => {
  // Use static value during SSR
  if (!isClient.value) return [1.05, 1]
  return isMobile.value ? [0.7, 0.9] : [1.05, 1]
})

// All animations start with neutral values for SSR
const rotate = computed(() => {
  if (!isClient.value) return 0
  return 20 * (1 - scrollYProgress.value)
})

const scale = computed(() => {
  if (!isClient.value) return 1 // Consistent SSR value
  const [start, end] = scaleDimensions.value
  return start + (end - start) * scrollYProgress.value
})

const translateY = computed(() => {
  if (!isClient.value) return 0
  return -100 * scrollYProgress.value
})
</script>
